import { Badge, Flex, Text } from '@mantine/core';
import { EditableTileContainer } from '@dealstack/frontend/shared/components/EditableTileContainer/EditableTileContainer.tsx';
import React, { useContext } from 'react';
import { EmploymentRecordRes } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS, makeComplexDataTestId } from '@dealstack/shared/tests/testElementIds';
import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { formatDateToUI } from '@dealstack/shared/lib/format/dates/date';

interface Props {
	employmentRecord: EmploymentRecordRes;
	onRemove: (record: EmploymentRecordRes) => void;
	onEdit: (record: EmploymentRecordRes) => void;
	hasEditPermission: boolean;
}

export const EmploymentRecordTile = ({
	employmentRecord,
	onRemove,
	onEdit,
	hasEditPermission,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const { personsSelectOptions } = useContext(CapManageControllerContext);

	const employerName =
		personsSelectOptions.find((option) => option.value === employmentRecord.employerId)
			?.label ?? '';
	const jobTitle = employmentRecord.jobTitle || '';
	const dateRange = [employmentRecord.startDate, employmentRecord.endDate]
		.map((date) => (date ? formatDateToUI(new Date(date)) : undefined))
		.filter(Boolean)
		.join(' – ');

	return (
		<EditableTileContainer
			hasEditPermission={hasEditPermission}
			onEditClick={() => onEdit(employmentRecord)}
			onDeleteClick={() => onRemove(employmentRecord)}
			dataTestId={makeComplexDataTestId(
				TEST_ELEMENT_IDS.CAP_EMPLOYMENT_RECORD_TILE,
				employmentRecord.id
			)}
			deleteBtnDataTestId={makeComplexDataTestId(
				TEST_ELEMENT_IDS.EDITABLE_TILE_DELETE_BUTTON,
				employmentRecord.id
			)}
			editBtnDataTestId={makeComplexDataTestId(
				TEST_ELEMENT_IDS.EDITABLE_TILE_EDIT_BUTTON,
				employmentRecord.id
			)}
		>
			<Flex direction="column" gap="0.25rem">
				<Flex gap="0.5rem" align="center">
					<Text fw={600} c="dark.8">
						{employerName ||
							tCapManage('basic_details.employment_records.unknown_employer')}
					</Text>
				</Flex>
				{jobTitle ? (
					<Text fw={400} c="dark.0">
						{jobTitle}
					</Text>
				) : null}
				{dateRange ? (
					<Text fw={400} c="dark.0">
						{dateRange}
					</Text>
				) : null}
				{employmentRecord.employeeReference ? (
					<Text fw={400} c="dark.0">
						{tCapManage('basic_details.employment_records.reference', {
							reference: employmentRecord.employeeReference,
						})}
					</Text>
				) : null}
				{employmentRecord.isPrimaryForEmployee ? (
					<Badge
						size="sm"
						variant="outline"
						color="gray.6"
						c="dark.4"
						fw={400}
						data-testid={makeComplexDataTestId(
							TEST_ELEMENT_IDS.CAP_EMPLOYMENT_RECORD_TILE_PRIMARY_BADGE,
							employmentRecord.id
						)}
					>
						{tCapManage('basic_details.employment_records.primary_badge')}
					</Badge>
				) : null}
			</Flex>
		</EditableTileContainer>
	);
};
